import { useLayoutEffect, useRef } from "react"

interface UseAutosizeTextAreaProps {
  ref: React.RefObject<HTMLTextAreaElement | null>
  maxHeight?: number
  dependencies: React.DependencyList
}

export function useAutosizeTextArea({
  ref,
  maxHeight = Number.MAX_SAFE_INTEGER,
  dependencies,
}: UseAutosizeTextAreaProps) {
  const minHeight = useRef<number | null>(null)
  const measuredWidth = useRef<number | null>(null)

  useLayoutEffect(() => {
    const textarea = ref.current
    if (!textarea) return

    const resize = () => {
      if (textarea.clientWidth === 0) return

      const borders = textarea.offsetHeight - textarea.clientHeight
      measuredWidth.current = textarea.clientWidth
      textarea.style.removeProperty("height")
      minHeight.current ??= textarea.scrollHeight - borders
      const fitted = Math.min(textarea.scrollHeight, maxHeight)
      textarea.style.height = `${Math.max(fitted, minHeight.current) + borders}px`
    }

    resize()

    // Resizing writes a height, which retriggers the observer — only widths are news
    const observer = new ResizeObserver(() => {
      if (textarea.clientWidth !== measuredWidth.current) {
        minHeight.current = null
        resize()
      }
    })
    observer.observe(textarea)
    return () => observer.disconnect()
  }, [maxHeight, ref, ...dependencies])
}
